/**
 * Admin "Draft nudges" panel — surfaces the daily 48h-stalled-draft
 * reactivation scheduler. Two controls: trigger a manual sweep (with
 * optional dry-run preview), and view recent sent nudges.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Send, RefreshCw, Eye } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso || "—"; }
};

export default function DraftNudgePanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/draft-nudge/history");
      setItems(data.items || []);
    } catch {
      toast.error("Failed to load nudge history");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const run = async (dryRun) => {
    setRunning(true);
    try {
      const { data } = await api.post(`/admin/draft-nudge/run${dryRun ? "?dry_run=true" : ""}`);
      if (dryRun) {
        toast.message(`Dry run · ${data.items.length} eligible · ${data.scanned} stalled drafts scanned`);
      } else if (data.sent > 0) {
        toast.success(`Nudges sent · ${data.sent} · ${data.skipped_dup} already-nudged · ${data.skipped_anonymous} anonymous`);
      } else {
        toast.message(`No nudges to send · ${data.scanned} stalled · ${data.skipped_dup} already-nudged · ${data.skipped_anonymous} anonymous`);
      }
      if (!dryRun) await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nudge run failed");
    } finally { setRunning(false); }
  };

  return (
    <div className="space-y-6" data-testid="draft-nudge-panel">
      <div>
        <div className="flex items-center justify-between mb-2">
          <div>
            <div className="ek-eyebrow text-[#7737F6] text-[11px]">Draft reactivation</div>
            <h2 className="ek-heading text-2xl text-black mt-1">48-hour stalled-draft nudge</h2>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => run(true)}
              disabled={running}
              className="ek-btn border border-zinc-300 text-zinc-800 hover:bg-zinc-50 inline-flex items-center gap-2 px-4 py-2 text-[13px]"
              data-testid="draft-nudge-dry-run"
            >
              <Eye size={14} /> {running ? "Scanning…" : "Dry run"}
            </button>
            <button
              type="button"
              onClick={() => run(false)}
              disabled={running}
              className="ek-btn ek-btn-primary inline-flex items-center gap-2 px-4 py-2 text-[13px]"
              data-testid="draft-nudge-run"
            >
              <Send size={14} /> {running ? "Sending…" : "Send nudges now"}
            </button>
          </div>
        </div>
        <p className="text-[14px] text-zinc-700 font-light max-w-3xl">
          Runs daily at 09:00 UTC. Finds anonymous lite-assessment drafts that have sat untouched for ≥48 hours, looks up the visitor&rsquo;s identified contact via <code className="text-[12px]">device_id</code>, and emails a Resend-branded resume link. One nudge per (device, slug) — once sent, the same draft will never be re-nudged.
        </p>
      </div>

      <div className="border border-zinc-200 bg-white">
        <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-200 bg-zinc-50">
          <div className="text-[12px] font-semibold tracking-[0.18em] uppercase text-zinc-700">Recent nudges</div>
          <button
            type="button"
            onClick={reload}
            className="text-[#7737F6] hover:text-[#5D28C7] text-[12px] inline-flex items-center gap-1"
            data-testid="draft-nudge-refresh"
          >
            <RefreshCw size={12} /> Refresh
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-[13px]">
            <thead className="bg-white">
              <tr className="text-left border-b border-zinc-200 text-[11px] uppercase tracking-wider text-zinc-500">
                <th className="px-4 py-2.5">Sent</th>
                <th className="px-4 py-2.5">Recipient</th>
                <th className="px-4 py-2.5">Sub-brand</th>
                <th className="px-4 py-2.5">Progress</th>
                <th className="px-4 py-2.5">Delivery</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-zinc-400 italic">Loading…</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={5} className="px-4 py-10 text-center text-zinc-500 text-[13px]">
                  <div className="font-medium text-zinc-700">No nudges sent yet.</div>
                  <div className="mt-1 text-zinc-500">When a visitor starts a lite assessment, shares their email, and then doesn&rsquo;t finish — that&rsquo;s when this scheduler kicks in.</div>
                </td></tr>
              ) : items.map((r, i) => (
                <tr key={i} className="border-t border-zinc-100" data-testid={`draft-nudge-row-${i}`}>
                  <td className="px-4 py-2 whitespace-nowrap">{fmtDate(r.sent_at)}</td>
                  <td className="px-4 py-2">
                    <div className="text-zinc-900">{r.name || <span className="text-zinc-500 italic">unnamed</span>}</div>
                    <div className="text-zinc-500 text-[12px]">{r.email}</div>
                  </td>
                  <td className="px-4 py-2 font-mono text-[12px]">{r.slug}</td>
                  <td className="px-4 py-2">{r.answered}/{r.total}</td>
                  <td className="px-4 py-2">
                    {r.sent ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 text-emerald-800 text-[11px] border border-emerald-200">sent</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 text-amber-800 text-[11px] border border-amber-200">skipped</span>
                    )}
                    {r.email_id ? <div className="text-zinc-400 text-[11px] font-mono mt-1">{r.email_id.slice(0, 8)}…</div> : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
